// UX-A (ux pass 0.5): the breadcrumb's page names, one registry instead of the top bar's hand-kept
// map. A page registers its label by mode and the path after the mode (`registerCrumb`); the
// crumb reads the longest registered prefix (`crumbOf`). A path segment that is an object's id
// (a space, a board, a channel, a meeting…) is registered as a `subject` crumb: the crumb shows
// that object's name from data already loaded (`useCachedName`), never a fetch of its own.
// Setup's pages are named by the Setup catalog; DE and seat pages by the focus chips' lookup.
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";

/** A crumb: a fixed label, or the name of the object whose id is the next path segment. */
export type CrumbEntry = { label: string } | { subject: string };

const entries = new Map<string, CrumbEntry>();

/** Names the page at `/c/{slug}/{mode}/{path}` (`path` without slashes at either end). */
export function registerCrumb(mode: string, path: string, entry: string | CrumbEntry): void {
  entries.set(`${mode}/${path}`, typeof entry === "string" ? { label: entry } : entry);
}

/**
 * The crumb for a path under a mode: the longest registered prefix of `rest` (up to three
 * segments). A key ending in `*` matches any one segment, the id a subject entry names
 * (`meetings/*`, `s/*`; `*` alone: `/chat/{channelId}`).
 */
export function crumbOf(
  mode: string,
  rest: readonly string[],
): { label: string } | { subject: string; id: string } | null {
  for (let n = Math.min(3, rest.length); n > 0; n--) {
    const exact = entries.get(`${mode}/${rest.slice(0, n).join("/")}`);
    if (exact && "label" in exact) return exact;
    const wild = entries.get(`${mode}/${[...rest.slice(0, n - 1), "*"].join("/")}`);
    const id = rest[n - 1];
    if (wild && "subject" in wild && id) return { subject: wild.subject, id };
  }
  return null;
}

/** Every registered key (tests). */
export function crumbKeys(): string[] {
  return [...entries.keys()].sort();
}

/** The `name` or `title` of the object with this id in any loaded query, else "". */
export function useCachedName(id: string | null): string {
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient],
  );
  const snapshot = () => {
    if (!id) return "";
    for (const q of queryClient.getQueryCache().getAll()) {
      const name = find(q.state.data, id, 0);
      if (name) return name;
    }
    return "";
  };
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

function find(value: unknown, id: string, depth: number): string | null {
  if (depth > 5 || value === null || typeof value !== "object") return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const name = find(item, id, depth + 1);
      if (name) return name;
    }
    return null;
  }
  const row = value as Record<string, unknown>;
  if (row.id === id) {
    if (typeof row.name === "string" && row.name) return row.name;
    if (typeof row.title === "string" && row.title) return row.title;
  }
  for (const v of Object.values(row)) {
    const name = find(v, id, depth + 1);
    if (name) return name;
  }
  return null;
}

// KPIs › Reports › Finance (M5-04; KPIS-MODE moved it from Chart, NAV-REDESIGN put Reports in
// KPIs). A week's report is named by the week.
registerCrumb("kpis", "reports/finance", "Budget vs actual");
registerCrumb("kpis", "reports/finance/margins", "Money per seat");
registerCrumb("kpis", "reports/finance/agent-spend", "Agent spend");
registerCrumb("kpis", "reports/finance/forecast", "Forecast");
registerCrumb("kpis", "reports/finance/scenarios", "Scenarios");
registerCrumb("kpis", "reports/finance/headcount", "Headcount plan");
registerCrumb("kpis", "reports/finance/exports", "Exports");

// KPIs (KPIS-MODE: Chart and Cadence as one mode).
registerCrumb("kpis", "chart", "Chart");
registerCrumb("kpis", "scorecard", "Scorecard");
registerCrumb("kpis", "my-numbers", "My numbers");
registerCrumb("kpis", "entry/hours", "Enter hours");
registerCrumb("kpis", "entry/numbers", "Enter numbers");
registerCrumb("kpis", "import", "Import CSV");
registerCrumb("kpis", "priorities", "Priorities");
registerCrumb("kpis", "issues", "Issues");
registerCrumb("kpis", "meetings", "Meetings");
registerCrumb("kpis", "vision", "Vision");
registerCrumb("kpis", "agents", "DE KPIs");
registerCrumb("kpis", "meetings/*", { subject: "meeting" });

// Workforce (UX-B: every page has a crumb; the roster, the mode's root, is "Digital employees" in
// top-bar.tsx; a DE by its name). Budgets and Skill packs are Setup's (the Setup catalog).
// HELPERS: Workforce › Agents is "Helpers" (one noun; the AG badge stays the type mark).
registerCrumb("agents", "ag", "Helpers");
registerCrumb("agents", "ag/*", { subject: "agent" });
registerCrumb("agents", "activity", "Activity");
registerCrumb("agents", "approvals", "Approvals");
registerCrumb("agents", "rooms", "Rooms");
registerCrumb("agents", "runs", "Runs");
registerCrumb("agents", "me", "My agent");
registerCrumb("agents", "skills", "Skill packs");
// HELPERS: the skill library (each skill once, with who uses it).
registerCrumb("agents", "skill-library", "Skills");
registerCrumb("agents", "delegations", "Delegation");
registerCrumb("agents", "templates", "Templates");

// Work (UX-A: every Work page had no crumb).
registerCrumb("work", "boards", "Boards");
registerCrumb("work", "my", "My tasks");
registerCrumb("work", "docs", "Docs");
registerCrumb("work", "automations", "Automations");
registerCrumb("work", "memory/review", "Memory review");
registerCrumb("work", "s/*", { subject: "space" });
registerCrumb("work", "b/*", { subject: "board" });
registerCrumb("work", "p/*", { subject: "page" });
registerCrumb("work", "t/*", { subject: "task" });

// Chat: its lists, then a channel by its name.
registerCrumb("chat", "browse", "Browse channels");
registerCrumb("chat", "unreads", "Unreads");
registerCrumb("chat", "people", "People");
registerCrumb("chat", "saved", "Saved");
registerCrumb("chat", "threads", "Threads");
registerCrumb("chat", "archived", "Archived channels");
registerCrumb("chat", "*", { subject: "channel" });

// Founder OS: a portfolio company by its name.
registerCrumb("portfolio", "companies/*", { subject: "company" });
